import React, { useEffect, useMemo, useState } from 'react';
import '../burn-motion.css';
import Segmented from './Segmented';
import WorkspaceState from './WorkspaceState';
import ExpenseColumn from './ExpenseColumn';
import ExpenseDialog from './ExpenseDialog';
import ConfirmExpenseDeleteDialog from './ConfirmExpenseDeleteDialog';
import { CATEGORIES, formatCents, monthlyBurnCents, sortByNextDue, todayYmd } from '../utils/expenseMath';

const FILTERS = [{ id: 'both', label: 'Both' }, ...CATEGORIES];
const SUMMARY_LABELS = {
  both: 'Combined monthly burn rate',
  personal: 'Personal monthly burn rate',
  business: 'Business monthly burn rate'
};

function BurnRateView({ workspaceReady, workspaceError, onRetryWorkspace, api }) {
  const [filter, setFilter] = useState('both');
  const [dialog, setDialog] = useState(null);
  const [today, setToday] = useState(todayYmd);
  const { items, status, error, refreshError, reload } = api;

  useEffect(() => {
    let midnightTimer;

    const scheduleMidnightRefresh = () => {
      window.clearTimeout(midnightTimer);

      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

      midnightTimer = window.setTimeout(() => {
        setToday(todayYmd());
        scheduleMidnightRefresh();
      }, nextMidnight.getTime() - now.getTime());
    };

    const refreshWhenReturning = () => {
      if (document.visibilityState === 'visible') {
        setToday(todayYmd());
      }
      scheduleMidnightRefresh();
    };

    scheduleMidnightRefresh();
    document.addEventListener('visibilitychange', refreshWhenReturning);
    window.addEventListener('focus', refreshWhenReturning);

    return () => {
      window.clearTimeout(midnightTimer);
      document.removeEventListener('visibilitychange', refreshWhenReturning);
      window.removeEventListener('focus', refreshWhenReturning);
    };
  }, []);

  const groups = useMemo(() => CATEGORIES.map((category) => {
    const list = items.filter((expense) => expense.category === category.id);
    return { ...category, list: sortByNextDue(list, today), total: monthlyBurnCents(list) };
  }), [items, today]);

  const visible = filter === 'both' ? groups : groups.filter((group) => group.id === filter);
  const total = visible.reduce((sum, group) => sum + group.total, 0);
  const closeDialog = () => setDialog(null);
  const openAdd = () => setDialog({ type: 'add' });
  const ready = !workspaceError && workspaceReady && status === 'ready';

  let content;
  if (workspaceError) {
    content = (
      <WorkspaceState
        role="alert"
        title="Couldn't load your workspace."
        actionLabel="Try again"
        onAction={onRetryWorkspace}
      />
    );
  } else if (status === 'error') {
    content = (
      <WorkspaceState
        role="alert"
        title="Couldn't load your expenses."
        text={error}
        actionLabel="Try again"
        onAction={reload}
      />
    );
  } else if (!ready) {
    content = <p className="burn-status" role="status">Loading expenses…</p>;
  } else if (items.length === 0) {
    content = (
      <WorkspaceState
        title="No recurring expenses yet"
        text="Add a recurring expense to see your monthly burn rate."
        actionLabel="Add expense"
        onAction={openAdd}
      />
    );
  } else {
    content = (
      <div key={filter} className={`burn-body${filter === 'both' ? '' : ' is-single'}`}>
        {refreshError && (
          <p className="burn-notice" role="status">
            Saved, but the expense list could not refresh: {refreshError}
            <button type="button" className="text-btn" onClick={reload}>Try again</button>
          </p>
        )}
        <div className="burn-columns">
          {visible.map((group) => (
            <ExpenseColumn
              key={group.id}
              group={group}
              onEdit={(expense) => setDialog({ type: 'edit', expense })}
              onDelete={(expense) => setDialog({ type: 'delete', expense })}
            />
          ))}
        </div>
        <div className="burn-summary" aria-live="polite">
          <span className="burn-summary-label">{SUMMARY_LABELS[filter]}</span>
          <strong className="burn-summary-value">{formatCents(total)}</strong>
          <span className="burn-summary-note">Monthly equivalent of recurring bills, not the amount due this month.</span>
        </div>
      </div>
    );
  }

  return (
    <section className="burn" aria-labelledby="burn-title">
      <div className="burn-head">
        <h1 id="burn-title" className="burn-title">Burn Rate</h1>
        {ready && items.length > 0 && (
          <div className="burn-controls">
            <Segmented label="Category filter" options={FILTERS} value={filter} onChange={setFilter} />
            <button type="button" className="btn btn-primary" onClick={openAdd}>Add expense</button>
          </div>
        )}
      </div>
      {content}

      {dialog?.type === 'add' && (
        <ExpenseDialog
          title="Add expense"
          submitLabel="Add"
          defaultCategory={filter === 'both' ? '' : filter}
          onSubmit={api.addExpense}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'edit' && (
        <ExpenseDialog
          title="Edit expense"
          submitLabel="Save"
          expense={dialog.expense}
          onSubmit={(values) => api.editExpense(dialog.expense.id, values)}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'delete' && (
        <ConfirmExpenseDeleteDialog
          expense={dialog.expense}
          onConfirm={api.removeExpense}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}

export default BurnRateView;